import { useState, useRef, useMemo, useEffect } from 'react';
import aviso, { textoDoErro } from '../lib/aviso';
import {
    LuCamera, LuTrash2, LuEye, LuEyeOff, LuCheck, LuKeyRound, LuX, LuInfo,
} from 'react-icons/lu';
import PageHeader from '../components/PageHeader';
import { apiFetch } from '../services/api';
import { UserPhotoService } from '../services/UserPhotoService';
import './ConfiguracoesContaPage.css';

const apiUrl = import.meta.env.VITE_API_URL || '';

interface Usuario {
    id: number;
    nome: string;
    email: string;
    role?: string;
    tipoUsuarioDescricao?: string;
    empresaDescricao?: string;
    photoUrl?: string | null;
}

const lerUsuario = (): Usuario | null => {
    try { return JSON.parse(localStorage.getItem('user') || 'null'); }
    catch { return null; }
};

const iniciais = (nome: string) => nome.trim().split(/\s+/).slice(0, 2)
    .map(p => p[0]?.toUpperCase() ?? '').join('') || '?';

const ConfiguracoesContaPage = () => {
    const [usuario] = useState<Usuario | null>(lerUsuario);

    const [foto, setFoto] = useState<string | null>(usuario?.photoUrl ?? null);
    /* A foto pode não carregar (em homologação a imagem não é servida). Sem
       isto o navegador mostra o texto alternativo, que parece defeito. */
    const [fotoFalhou, setFotoFalhou] = useState(false);
    const [mexendoNaFoto, setMexendoNaFoto] = useState(false);
    const refArquivo = useRef<HTMLInputElement>(null);

    const [modalSenha, setModalSenha] = useState(false);

    /* ─── Foto ───────────────────────────────────────────────── */

    const guardarFoto = (url: string | null) => {
        setFoto(url);
        setFotoFalhou(false);
        const guardado = JSON.parse(localStorage.getItem('user') || '{}');
        guardado.photoUrl = url;
        localStorage.setItem('user', JSON.stringify(guardado));
        // Quem mostra o avatar em outros cantos ouve este evento.
        window.dispatchEvent(new CustomEvent('userPhotoUpdated', { detail: { photoUrl: url } }));
    };

    const trocarFoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const arquivo = e.target.files?.[0];
        if (!arquivo) return;
        setMexendoNaFoto(true);
        try {
            const r = await UserPhotoService.upload(arquivo);
            guardarFoto(r.dados.photoUrl);
            aviso.ok('Foto de perfil atualizada.');
        } catch (err: any) {
            aviso.erro(textoDoErro(err, 'Não foi possível atualizar a foto.'));
        } finally {
            setMexendoNaFoto(false);
            if (refArquivo.current) refArquivo.current.value = '';
        }
    };

    const removerFoto = async () => {
        setMexendoNaFoto(true);
        try {
            await UserPhotoService.remover();
            guardarFoto(null);
            aviso.ok('Foto de perfil removida.');
        } catch (err: any) {
            aviso.erro(textoDoErro(err, 'Não foi possível remover a foto.'));
        } finally {
            setMexendoNaFoto(false);
        }
    };

    const mostrarFoto = !!foto && !fotoFalhou;

    return (
        <div className="page-shell cc-screen">
            <PageHeader
                centered
                title="Configurações da conta"
                subtitle="Seus dados de acesso à plataforma."
            />

            <div className="page-body">
                {/* Coluna centralizada: a tela tem pouca coisa, e alinhada à
                    esquerda num monitor largo o conteúdo fica perdido na
                    quina com a tela inteira vazia à direita. */}
                <div className="cc-coluna">

                    {/* Identidade num bloco só: foto, nome e e-mail são a mesma
                        informação. Como linhas separadas de rótulo e valor
                        viravam quatro itens soltos. */}
                    <section className="cc-cartao">
                        <div className="cc-id">
                            {mostrarFoto
                                ? <img className="cc-foto" src={foto!} alt=""
                                    onError={() => setFotoFalhou(true)} />
                                : <span className="cc-foto cc-foto-vazia" aria-hidden="true">
                                    {iniciais(usuario?.nome || '?')}
                                  </span>}

                            <div className="cc-id-txt">
                                <h2 className="cc-nome">{usuario?.nome || '—'}</h2>
                                <p className="cc-email">{usuario?.email || '—'}</p>
                            </div>

                            <div className="cc-id-acoes">
                                <input ref={refArquivo} type="file" accept="image/*"
                                    hidden onChange={trocarFoto} />
                                <button type="button" className="cc-btn" disabled={mexendoNaFoto}
                                    onClick={() => refArquivo.current?.click()}>
                                    <LuCamera size={13} /> {mostrarFoto ? 'Trocar' : 'Enviar foto'}
                                </button>
                                {mostrarFoto && (
                                    <button type="button" className="cc-btn is-danger" disabled={mexendoNaFoto}
                                        onClick={removerFoto} aria-label="Remover foto">
                                        <LuTrash2 size={13} />
                                    </button>
                                )}
                            </div>
                        </div>

                        <dl className="cc-linhas">
                            <div className="cc-linha">
                                <dt>Empresa</dt>
                                <dd>{usuario?.empresaDescricao || '—'}</dd>
                            </div>
                            <div className="cc-linha">
                                <dt>Perfil de acesso</dt>
                                <dd>{usuario?.tipoUsuarioDescricao || usuario?.role || '—'}</dd>
                            </div>
                        </dl>

                        <p className="cc-nota">
                            <LuInfo size={13} />
                            Nome, e-mail e perfil de acesso são definidos por quem administra os
                            usuários da empresa.
                        </p>
                    </section>

                    {/* A senha virou uma linha com botão: o formulário inteiro
                        aberto na tela era o que mais pesava, para uma coisa que
                        se faz uma vez por ano. */}
                    <section className="cc-cartao">
                        <div className="cc-acao">
                            <span className="cc-acao-ico"><LuKeyRound size={16} /></span>
                            <div className="cc-acao-txt">
                                <h2 className="cc-acao-tit">Senha</h2>
                                <p className="cc-acao-sub">
                                    Usada para entrar na plataforma com o seu e-mail.
                                </p>
                            </div>
                            <button type="button" className="cc-btn" onClick={() => setModalSenha(true)}>
                                Alterar senha
                            </button>
                        </div>
                    </section>

                </div>
            </div>

            {modalSenha && (
                <ModalSenha
                    usuario={usuario}
                    onFechar={() => setModalSenha(false)}
                />
            )}
        </div>
    );
};

/* ─── Modal de troca de senha ────────────────────────────────── */

const ModalSenha = ({ usuario, onFechar }: { usuario: Usuario | null; onFechar: () => void }) => {
    const [atual, setAtual] = useState('');
    const [nova, setNova] = useState('');
    const [confirmacao, setConfirmacao] = useState('');
    const [verAtual, setVerAtual] = useState(false);
    const [verNova, setVerNova] = useState(false);
    const [verConfirmacao, setVerConfirmacao] = useState(false);
    const [erro, setErro] = useState<string | null>(null);
    const [salvando, setSalvando] = useState(false);

    useEffect(() => {
        const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onFechar(); };
        document.addEventListener('keydown', esc);
        return () => document.removeEventListener('keydown', esc);
    }, [onFechar]);

    /* As cinco regras vêm verbatim da modal antiga: mudá-las aqui faria a tela
       aceitar senha que a API recusa, ou o contrário. */
    const regras = useMemo(() => [
        { rotulo: 'Entre 8 e 16 caracteres', ok: nova.length >= 8 && nova.length <= 16 },
        { rotulo: 'Uma letra maiúscula', ok: /[A-Z]/.test(nova) },
        { rotulo: 'Uma letra minúscula', ok: /[a-z]/.test(nova) },
        { rotulo: 'Um caractere especial', ok: /[!@#$%^&*(),.?":{}|<>]/.test(nova) },
    ], [nova]);

    const salvar = async (e: React.FormEvent) => {
        e.preventDefault();
        setErro(null);

        if (!atual) { setErro('A senha atual é obrigatória.'); return; }
        if (!nova) { setErro('A nova senha é obrigatória.'); return; }
        if (nova !== confirmacao) { setErro('As senhas não conferem.'); return; }
        if (!regras.every(r => r.ok)) {
            setErro('A nova senha não atende a todos os requisitos.');
            return;
        }

        setSalvando(true);
        try {
            const resposta = await apiFetch(`${apiUrl}/api/Usuario/AtualizarSenha/${usuario?.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: usuario?.email, senhaAtual: atual, novaSenha: nova }),
            });
            // A API às vezes responde sem corpo: `text()` antes do parse.
            const texto = await resposta.text();
            const dados = texto ? JSON.parse(texto) : null;
            if (!resposta.ok || (dados && !dados.sucesso)) {
                throw new Error((dados && dados.mensagem) || 'Erro ao atualizar a senha.');
            }
            aviso.ok('Senha atualizada.');
            onFechar();
        } catch (err: any) {
            setErro(err.message || 'Erro de conexão ao atualizar a senha.');
        } finally {
            setSalvando(false);
        }
    };

    const campo = (
        id: string, rotulo: string, valor: string, setValor: (v: string) => void,
        visivel: boolean, setVisivel: (v: boolean) => void,
    ) => (
        <div className="cc-campo">
            <label className="cc-lab" htmlFor={id}>{rotulo}</label>
            <div className="cc-senha">
                <input id={id} className="cc-inp" type={visivel ? 'text' : 'password'}
                    value={valor} autoComplete="off" onChange={e => setValor(e.target.value)} />
                <button type="button" className="cc-olho" onClick={() => setVisivel(!visivel)}
                    aria-label={visivel ? 'Ocultar senha' : 'Mostrar senha'}>
                    {visivel ? <LuEyeOff size={14} /> : <LuEye size={14} />}
                </button>
            </div>
        </div>
    );

    return (
        <div className="cc-overlay" onClick={onFechar}>
            <div className="cc-modal" role="dialog" aria-modal="true" aria-label="Alterar senha"
                onClick={e => e.stopPropagation()}>
                <header className="cc-modal-head">
                    <h2>Alterar senha</h2>
                    <button type="button" className="cc-fechar" onClick={onFechar} aria-label="Fechar">
                        <LuX size={14} />
                    </button>
                </header>

                <form className="cc-modal-corpo" onSubmit={salvar}>
                    {campo('cc-atual', 'Senha atual', atual, setAtual, verAtual, setVerAtual)}
                    {campo('cc-nova', 'Nova senha', nova, setNova, verNova, setVerNova)}

                    <ul className="cc-regras">
                        {regras.map(r => (
                            <li key={r.rotulo} className={r.ok ? 'is-ok' : ''}>
                                <LuCheck size={12} /> {r.rotulo}
                            </li>
                        ))}
                    </ul>

                    {campo('cc-confirma', 'Confirmar nova senha', confirmacao, setConfirmacao,
                        verConfirmacao, setVerConfirmacao)}

                    {erro && <p className="cc-erro">{erro}</p>}

                    <div className="cc-modal-pe">
                        <button type="button" className="cc-btn" onClick={onFechar} disabled={salvando}>
                            Cancelar
                        </button>
                        <button type="submit" className="cc-btn is-primary" disabled={salvando}>
                            {salvando ? 'Salvando…' : 'Salvar senha'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ConfiguracoesContaPage;
